// "What to include" for exports. Every format (backup, GEDCOM, CSV, image, PDF, HTML) gets a
// copy of the tree with the excluded details removed, so one set of choices means the same
// thing everywhere.

import { isHiddenName, isLiving } from '../model/people';
import type { Id, MediaItem, Person, Relationship, Tree } from '../model/types';
import { treePlaces } from '../model/places';

export interface ExportPrivacy {
  /** Living people become "Living" with no details (relationships kept for the structure). */
  privatizeLiving: boolean;
  /** Hidden names and deadnames. */
  hiddenNames: boolean;
  photos: boolean;
  /** Gender, pronouns, and relationship-word choices. */
  identity: boolean;
  dates: boolean;
  places: boolean;
  /** Life events other than birth and death. */
  otherEvents: boolean;
  notes: boolean;
  tags: boolean;
  /** Friendships and other social ties, and groups. */
  friendsAndGroups: boolean;
  /** Sources and the citations that point at them. */
  sources: boolean;
  /** Photos, documents, recordings, and links in the Media view (profile photos are `photos`). */
  media: boolean;
  todos: boolean;
}

export const INCLUDE_EVERYTHING: ExportPrivacy = {
  privatizeLiving: false,
  hiddenNames: true,
  photos: true,
  identity: true,
  dates: true,
  places: true,
  otherEvents: true,
  notes: true,
  tags: true,
  friendsAndGroups: true,
  sources: true,
  media: true,
  todos: true,
};

export const PRIVACY_LABELS: { key: keyof ExportPrivacy; label: string; hint?: string }[] = [
  { key: 'photos', label: 'Photos' },
  { key: 'identity', label: 'Gender and pronouns' },
  { key: 'dates', label: 'Dates' },
  { key: 'places', label: 'Places' },
  { key: 'otherEvents', label: 'Life events besides birth and death' },
  { key: 'notes', label: 'Notes', hint: 'On people and relationships' },
  { key: 'tags', label: 'Tags' },
  { key: 'friendsAndGroups', label: 'Friends, other connections, and groups' },
  { key: 'sources', label: 'Sources and citations' },
  { key: 'media', label: 'Media', hint: 'Photos, documents, recordings, and links in the Media view' },
  { key: 'todos', label: 'To-dos' },
  { key: 'hiddenNames', label: 'Hidden names and deadnames', hint: 'Anyone who opens the file will see them' },
];

function redactPerson(p: Person, o: ExportPrivacy): Person {
  if (o.privatizeLiving && isLiving(p)) {
    return { id: p.id, names: [{ given: 'Living', surname: '', type: 'name' }], gender: o.identity ? { identity: p.gender.identity === 'custom' ? 'unknown' : p.gender.identity } : { identity: 'unknown' }, living: true, events: [], notes: '', tags: [], gedcomId: p.gedcomId };
  }
  let names = o.hiddenNames ? p.names : p.names.filter((n) => !isHiddenName(n));
  if (!names.length) names = [{ given: '', surname: '', type: 'name' }];
  const events = p.events
    .filter((e) => o.otherEvents || ['birth', 'death', 'burial', 'cremation'].includes(e.type))
    .map((e) => ({ ...e, date: o.dates ? e.date : undefined, place: o.places ? e.place : undefined, note: o.notes ? e.note : undefined }))
    // An event with nothing left but its type still says it happened (a death), so keep deaths.
    .filter((e) => e.date || e.place || e.note || e.label || e.type !== 'birth');
  const out: Person = { ...p, names, events, notes: o.notes ? p.notes : '', tags: o.tags ? p.tags : [] };
  if (!o.sources) {
    delete out.citations;
    out.events = out.events.map(({ citations: _, ...e }) => e);
  }
  // Preserved GEDCOM records can hold dates, places, and notes of their own, so they go too.
  if (!o.dates || !o.places || !o.notes) {
    delete out.passthrough;
    out.events = out.events.map(({ passthrough: _, ...e }) => e);
  }
  if (!o.photos) delete out.photo;
  if (!o.identity) {
    out.gender = { identity: 'unknown' };
    delete out.pronouns;
    delete out.terms;
  }
  return out;
}

function redactRel(r: Relationship, o: ExportPrivacy): Relationship {
  const out = { ...r };
  if (!o.dates) {
    delete out.start;
    delete out.end;
  }
  if (!o.places) delete out.place;
  if (!o.notes) delete out.notes;
  if (!o.sources) delete out.citations;
  return out;
}

/** Whether these choices leave nothing out, so a Relationships file made with them is a full backup. */
export function includesEverything(o: ExportPrivacy): boolean {
  return (Object.keys(INCLUDE_EVERYTHING) as (keyof ExportPrivacy)[]).every((k) => o[k] === INCLUDE_EVERYTHING[k]);
}

export function redactTree(tree: Tree, o: ExportPrivacy): Tree {
  const people: Tree['people'] = {};
  for (const p of Object.values(tree.people)) people[p.id] = redactPerson(p, o);
  const relationships: Tree['relationships'] = {};
  for (const r of Object.values(tree.relationships)) {
    if (!o.friendsAndGroups && r.kind === 'social') continue;
    const living = o.privatizeLiving && (isLiving(tree.people[r.a]) || isLiving(tree.people[r.b]));
    relationships[r.id] = living ? { ...redactRel(r, { ...o, dates: false, places: false, notes: false, sources: false }) } : redactRel(r, o);
  }
  // Living people who are privatized drop out of media and to-dos; a picture of only them goes.
  const hidden = (id: Id) => o.privatizeLiving && !!tree.people[id] && isLiving(tree.people[id]);
  const media: Record<Id, MediaItem> = {};
  if (o.media)
    for (const m of Object.values(tree.media ?? {})) {
      const shown = m.people.filter((x) => !hidden(x.personId));
      if (m.people.length && !shown.length) continue;
      media[m.id] = { ...m, people: shown, ...(o.sources ? {} : { sourceIds: undefined }), ...(o.dates ? {} : { date: undefined }), ...(o.notes ? {} : { description: undefined }) };
    }
  const todos: Tree['todos'] = {};
  if (o.todos) for (const td of Object.values(tree.todos ?? {})) todos[td.id] = { ...td, people: td.people.filter((id) => !hidden(id)) };
  const out: Tree = { ...tree, people, relationships, groups: o.friendsAndGroups ? tree.groups : {}, sources: o.sources ? tree.sources ?? {} : {}, media, todos };
  // Only the locations of places still in the file: the rest would say where left-out events happened.
  const used = new Set(treePlaces(out).map((p) => p.key));
  const locations = Object.entries(tree.locations ?? {}).filter(([key]) => used.has(key));
  return { ...out, locations: locations.length ? Object.fromEntries(locations) : undefined };
}
